import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useYearbook } from '../context/YearbookContext';
import { useFunnyLoadingText } from '../hooks';
import { getParticipants, saveNameMappings, getNameMappings } from '../api/client';
import { Analytics } from '../utils/analytics';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { StatisticsDisplay } from '../components/features';

export default function AnalysisPage() {
  const navigate = useNavigate();
  const { isInitialized, stats, sessionId } = useYearbook();
  
  // Participant editor state
  const [participants, setParticipants] = useState([]);
  const [editingName, setEditingName] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [selectedForMerge, setSelectedForMerge] = useState(new Set());
  const [nameMappings, setNameMappings] = useState({});
  const [showParticipantEditor, setShowParticipantEditor] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const funnyLoadingText = useFunnyLoadingText();
  
  // Redirect to home if no valid session (after initialization)
  useEffect(() => {
    if (isInitialized && !stats) {
      navigate('/');
    }
  }, [isInitialized, stats, navigate]);
  
  // Load participants when editor is opened
  const loadParticipants = useCallback(async () => {
    if (!sessionId) return;
    
    setLoadingParticipants(true);
    try {
      const [participantsData, mappingsData] = await Promise.all([
        getParticipants(sessionId),
        getNameMappings(sessionId).catch(() => ({ mappings: [] }))
      ]);
      
      setParticipants(participantsData.participants);
      
      // Convert mappings array to object
      const mappingsObj = {};
      mappingsData.mappings.forEach(m => {
        mappingsObj[m.original_name] = m.new_name;
      });
      setNameMappings(mappingsObj);
    } catch (error) {
      console.error('Failed to load participants:', error);
    } finally {
      setLoadingParticipants(false);
    }
  }, [sessionId]);
  
  useEffect(() => {
    if (showParticipantEditor && participants.length === 0) {
      loadParticipants();
    }
  }, [showParticipantEditor, participants.length, loadParticipants]);
  
  // Load saved name mappings on mount so collapsed view shows mapped names
  useEffect(() => {
    if (!sessionId) return;
    getNameMappings(sessionId)
      .then((data) => {
        const mappingsObj = {};
        data.mappings.forEach(m => {
          mappingsObj[m.original_name] = m.new_name;
        });
        setNameMappings(mappingsObj);
      })
      .catch(() => {});
  }, [sessionId]);
  
  if (!stats) {
    return null;
  }
  
  const handleContinue = () => {
    Analytics.trackAnalysisComplete(stats?.years?.length || 0);
    navigate('/book-setup');
  };
  
  // Format number with commas
  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  // Format date as "Mar 19, 2022" for hero "Since ..."
  const formatSinceDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Days between start and end for tagline
  const chatDays = (() => {
    if (!stats?.date_range_start || !stats?.date_range_end) return 0;
    const start = new Date(stats.date_range_start);
    const end = new Date(stats.date_range_end);
    return Math.round((end - start) / (24 * 60 * 60 * 1000));
  })();
  
  // Participant editing functions
  const startEditing = (participant) => {
    setEditingName(participant.name);
    setEditValue(nameMappings[participant.name] || participant.name);
  };
  
  const cancelEditing = () => {
    setEditingName(null);
    setEditValue('');
  };
  
  const saveEdit = async () => {
    if (!editingName || !editValue.trim()) {
      cancelEditing();
      return;
    }
    
    const newMappings = { ...nameMappings };
    
    if (editValue.trim() === editingName) {
      // Remove mapping if same as original
      delete newMappings[editingName];
    } else {
      newMappings[editingName] = editValue.trim();
    }
    
    setNameMappings(newMappings);
    cancelEditing();
  };
  
  const toggleMergeSelection = (participant) => {
    const newSelection = new Set(selectedForMerge);
    if (newSelection.has(participant.name)) {
      newSelection.delete(participant.name);
    } else {
      newSelection.add(participant.name);
    }
    setSelectedForMerge(newSelection);
  };
  
  const mergeSelected = () => {
    if (selectedForMerge.size < 2) return;
    
    // Find the participant with the most messages to use as the canonical name
    const selectedParticipants = participants.filter(p => selectedForMerge.has(p.name));
    const canonical = selectedParticipants.reduce((a, b) => 
      (a.message_count > b.message_count) ? a : b
    );
    
    // Create mappings for all selected to point to canonical
    const newMappings = { ...nameMappings };
    selectedForMerge.forEach(name => {
      if (name !== canonical.name) {
        newMappings[name] = canonical.name;
      }
    });
    
    setNameMappings(newMappings);
    setSelectedForMerge(new Set());
  };
  
  const saveMappings = () => {
    if (!sessionId) return;
    
    let finalMappings = { ...nameMappings };
    
    // Commit any in-progress inline edit before persisting
    if (editingName && editValue.trim() && editValue.trim() !== editingName) {
      finalMappings[editingName] = editValue.trim();
    } else if (editingName && editValue.trim() === editingName) {
      delete finalMappings[editingName];
    }
    
    setNameMappings(finalMappings);
    setEditingName(null);
    setEditValue('');
    setShowParticipantEditor(false);
    
    const mappingsArray = Object.entries(finalMappings).map(([original, newName]) => ({
      original_name: original,
      new_name: newName,
    }));
    
    saveNameMappings(sessionId, mappingsArray).catch(error => {
      console.error('Failed to save name mappings:', error);
    });
  };
  
  // Get display name for a participant
  const getDisplayName = (participant) => {
    return nameMappings[participant.name] || participant.name;
  };
  
  // Check if name looks like a phone number
  const looksLikePhoneNumber = (name) => {
    const cleaned = name.replace(/[\s\-()]/g, '');
    return /^\+?\d{7,15}$/.test(cleaned);
  };
  
  // Count unique display names after mappings
  const getUniqueParticipantCount = () => {
    const displayNames = new Set(participants.map(p => getDisplayName(p)));
    return displayNames.size;
  };
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-12 bg-cream paper-bg min-h-full">
      {/* Dashboard: Hero + stats sections + tagline */}
      <div className="space-y-6 mb-10">
        {/* Hero section — top-stripe accent, centered */}
        <div className="bg-white rounded-xl border border-parchment/30 border-t-[3px] border-t-rust p-6 sm:p-8 text-center">
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-rust mb-3">
            {stats.group_name || 'Your Chat'} in Numbers
          </h2>
          <p className="text-2xl sm:text-3xl font-bold text-ink leading-tight">
            {formatNumber(stats.total_messages)} messages
            <span className="text-muted font-normal"> · </span>
            {stats.years.length} years
            <span className="text-muted font-normal"> · </span>
            {stats.participant_count} participants
          </p>
          <p className="text-sm text-muted mt-3">
            Since {formatSinceDate(stats.date_range_start)}
          </p>
        </div>

        {/* Stats sections (renders its own section grids) */}
        <StatisticsDisplay sessionId={sessionId} />

        {/* Tagline — conclusion card */}
        <div className="bg-white rounded-xl border border-parchment/30 p-6 sm:p-8 text-center">
          <p className="text-lg font-medium text-ink">
            {chatDays.toLocaleString()} days of memories together ❤️
          </p>
        </div>
      </div>
      
      {/* Participants Section */}
      <div className="card mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-ink">Participants</h3>
          <button
            onClick={() => setShowParticipantEditor(!showParticipantEditor)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-rust hover:text-rust/80 
                       hover:bg-rust/5 rounded-lg transition-colors"
          >
            <PencilIcon className="w-4 h-4" />
            {showParticipantEditor ? 'Hide Editor' : 'Edit Names'}
          </button>
        </div>
        
        {!showParticipantEditor ? (
          // Simple participant list
          <div className="flex flex-wrap gap-2">
            {stats.participants.map((name) => (
              <span
                key={name}
                className={`px-3 py-1.5 rounded-full text-sm ${
                  looksLikePhoneNumber(name)
                    ? 'bg-gold/10 text-gold border border-gold/30'
                    : 'bg-parchment/50 text-sepia'
                }`}
              >
                {nameMappings[name] || name}
                {nameMappings[name] && nameMappings[name] !== name && (
                  <span className="text-xs text-sepia/60 ml-1">(was {name})</span>
                )}
              </span>
            ))}
          </div>
        ) : (
          // Participant editor
          <div className="space-y-4">
            {loadingParticipants ? (
              <div className="text-center py-8">
                <p className="text-sepia mb-2">Loading participants...</p>
                <p className="text-sm text-sepia/70 italic">{funnyLoadingText}</p>
              </div>
            ) : (
              <>
                {/* Merge controls */}
                {selectedForMerge.size >= 2 && (
                  <div className="flex items-center gap-3 p-3 bg-rust/5 rounded-lg border border-rust/20">
                    <ArrowsRightLeftIcon className="w-5 h-5 text-rust" />
                    <span className="text-sm text-ink flex-1">
                      {selectedForMerge.size} participants selected for merge
                    </span>
                    <button
                      onClick={mergeSelected}
                      className="px-3 py-1 bg-rust text-white text-sm rounded-lg hover:bg-rust/90"
                    >
                      Merge Selected
                    </button>
                    <button
                      onClick={() => setSelectedForMerge(new Set())}
                      className="px-3 py-1 text-sepia text-sm hover:text-ink"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                
                {/* Participant list */}
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {participants.map((participant) => {
                    const isEditing = editingName === participant.name;
                    const isSelected = selectedForMerge.has(participant.name);
                    const displayName = getDisplayName(participant);
                    const hasMapping = nameMappings[participant.name] && 
                                       nameMappings[participant.name] !== participant.name;
                    const isPhone = looksLikePhoneNumber(participant.name);
                    
                    return (
                      <div
                        key={participant.name}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          isSelected 
                            ? 'bg-rust/5 border-rust/30' 
                            : 'bg-parchment/30 border-transparent hover:border-sepia/20'
                        }`}
                      >
                        {/* Checkbox for merge */}
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleMergeSelection(participant)}
                          className="w-4 h-4 rounded border-sepia/30 text-rust 
                                     focus:ring-rust focus:ring-offset-0"
                        />
                        
                        {/* Name display/edit */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit();
                                if (e.key === 'Escape') cancelEditing();
                              }}
                              autoFocus
                              className="w-full px-2 py-1 text-sm border border-rust/30 
                                         rounded focus:outline-none focus:ring-2 focus:ring-rust/20"
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isPhone && !hasMapping ? 'text-gold' : 'text-ink'}`}>
                                {displayName}
                              </span>
                              {hasMapping && (
                                <span className="text-xs text-sepia/60">
                                  (was: {participant.name})
                                </span>
                              )}
                              {isPhone && !hasMapping && (
                                <span className="text-xs px-2 py-0.5 bg-gold/10 text-gold rounded-full">
                                  phone
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Message count */}
                        <span className="text-xs text-sepia whitespace-nowrap">
                          {formatNumber(participant.message_count)} msgs
                        </span>
                        
                        {/* Edit/Save buttons */}
                        {isEditing ? (
                          <div className="flex gap-1">
                            <button
                              onClick={saveEdit}
                              className="p-1 text-forest hover:bg-forest/10 rounded"
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-1 text-sepia hover:bg-sepia/10 rounded"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditing(participant)}
                            className="p-1 text-sepia hover:text-rust hover:bg-rust/5 rounded"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Summary and save */}
                <div className="flex items-center justify-between pt-3 border-t border-sepia/10">
                  <div className="text-sm text-sepia">
                    {Object.keys(nameMappings).length > 0 ? (
                      <>
                        {Object.keys(nameMappings).length} name(s) changed
                        {participants.length !== getUniqueParticipantCount() && (
                          <> • {participants.length - getUniqueParticipantCount()} merged</>
                        )}
                      </>
                    ) : (
                      'No changes made'
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setNameMappings({});
                        setSelectedForMerge(new Set());
                      }}
                      className="px-3 py-1.5 text-sm text-sepia hover:text-ink"
                    >
                      Reset All
                    </button>
                    <button
                      onClick={saveMappings}
                      className="px-4 py-1.5 bg-rust text-white text-sm rounded-lg 
                                 hover:bg-rust/90"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Continue Button */}
      <div className="flex justify-center">
        <button
          onClick={handleContinue}
          className="btn-primary text-lg px-10 py-4"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
